import React, { MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import PinLegendVisibility from "@mapmycustomers/shared/enum/PinLegendVisibility";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import { getCurrentUserId, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import canEditPinLegend from "../utils/canEditPinLegend";

import styles from "./PinLegendLabel.module.scss";

interface Props {
  currentUserId: User["id"];
  isOwner?: boolean;
  onEdit?: (pinLegend: PinLegend) => void;
  pinLegend: PinLegend;
}

const PinLegendLabel: React.FC<Props> = ({ currentUserId, isOwner, onEdit, pinLegend }) => {
  const intl = useIntl();

  const handleEditClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onEdit?.(pinLegend);
    },
    [onEdit, pinLegend]
  );

  // legend is editable when...
  const isEditable = canEditPinLegend(pinLegend, currentUserId, isOwner);

  const hasActions = isEditable;

  return (
    <div className={styles.container}>
      <span className={styles.name}>
        {pinLegend.name}{" "}
        {
          // we're not showing this tag for default pin legends
          !pinLegend.default && pinLegend.visibility !== PinLegendVisibility.PRIVATE && (
            <Tag color="default">
              {intl.formatMessage({
                id: "map.toolbar.popovers.pinSettings.pinLegendOption.sharedTag",
                defaultMessage: "Shared",
                description:
                  "A tag shown when pin legend is shared in Pin Select field in Pin Settings popover on the map page",
              })}
            </Tag>
          )
        }
      </span>
      <span className={styles.right}>
        {hasActions && (
          <span className={styles.actions}>
            {isEditable && (
              <Tooltip
                title={intl.formatMessage({
                  id: "map.toolbar.popovers.pinSettings.pinLegendOption.edit.tooltip",
                  defaultMessage: "Edit Legend",
                  description:
                    "A tooltip of the Edit button in Pin Select field in Pin Settings popover on the map page",
                })}
              >
                <Button
                  className={styles.button}
                  icon={<FontAwesomeIcon icon={faPen} />}
                  iconOnly
                  onClick={handleEditClick}
                  size="small"
                  type="text"
                />
              </Tooltip>
            )}
          </span>
        )}
      </span>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  isOwner: isCurrentUserOwner(state),
});

export default connect(mapStateToProps, {})(PinLegendLabel);
